import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectOverviewValuesHidden,
  toggleOverviewValuesHidden,
} from '../../../store/slices/ui';

/**
 * Provider kept as a passthrough for backwards compatibility — hidden state
 * lives in Redux now, so any component anywhere in the tree can read it via
 * `useValueHide()` without needing to be wrapped.
 */
export const ValueHideProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <>{children}</>;

export const useValueHide = () => {
  const dispatch = useDispatch();
  const hidden = useSelector(selectOverviewValuesHidden);
  const toggle = useCallback(() => {
    dispatch(toggleOverviewValuesHidden());
  }, [dispatch]);
  return { hidden, toggle };
};

interface HiddenValueProps {
  children: React.ReactNode;
  /** Legacy prop, ignored — width is taken from the children's natural width
   *  to guarantee zero layout shift when toggling visibility. */
  width?: number;
}

const MATRIX_GLYPHS = 'ｱｲｳｴｵｶｷｸｹｺ0123456789$€¥%';
const randomGlyph = () =>
  MATRIX_GLYPHS[Math.floor(Math.random() * MATRIX_GLYPHS.length)];
const randomString = (length: number) => {
  let s = '';
  for (let i = 0; i < length; i++) s += randomGlyph();
  return s;
};

/**
 * HiddenValue
 *
 * When `hidden` is true, the original `children` are still rendered (visibility
 * hidden) so that the parent's layout is preserved exactly. A matrix-style
 * overlay is absolutely positioned on top using `currentColor`, so the value
 * inherits whatever color its label / row context defines (positive green,
 * negative red, neutral white, etc.).
 */
export const HiddenValue: React.FC<HiddenValueProps> = ({ children }) => {
  const hidden = useSelector(selectOverviewValuesHidden);

  // Always 6 random matrix glyphs. Frozen for the lifetime of this component
  // instance so re-renders don't cause flicker, but each instance gets its
  // own unique sequence on mount.
  const text = useMemo(() => randomString(6), []);

  if (!hidden) return <>{children}</>;

  return (
    <span className="matrix-hidden-wrap" aria-label="hidden">
      <span className="matrix-hidden-spacer">{children}</span>
      <span className="matrix-hidden-overlay" aria-hidden="true">
        {text}
      </span>
    </span>
  );
};
